import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  CheckSquare,
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  Paperclip,
  Calendar,
  Tag,
  Eye,
  Save,
  Loader2,
  Clock,
} from 'lucide-react';
import { complianceExecutionApi, CompliancePage, EvidenceItem } from '@domains/compliance/execution/api/complianceExecution.api';
import { notify } from '@core/notifications/notify';
import FindingsJournal from '@components/FindingsJournal';
import PageInfoButton from '@/components/PageInfoButton';

type FieldResponseValue = string | string[] | null;

const parseFieldOptions = (fieldOptions: any): Array<{ value: string; label: string }> => {
  let parsedOptions = fieldOptions;

  if (typeof parsedOptions === 'string') {
    try {
      parsedOptions = JSON.parse(parsedOptions);
    } catch {
      parsedOptions = [];
    }
  }

  if (parsedOptions && !Array.isArray(parsedOptions) && Array.isArray(parsedOptions.options)) {
    parsedOptions = parsedOptions.options;
  }

  if (!Array.isArray(parsedOptions)) return [];

  return parsedOptions
    .map((opt: any) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }

      const value = String(opt?.value ?? opt?.label ?? '').trim();
      const label = String(opt?.label ?? opt?.value ?? '').trim();
      if (!value || !label) return null;
      return { value, label };
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;
};

const isMultiSelectField = (field: any): boolean => {
  if (!field) return false;
  if (field.field_type === 'multiselect') return true;
  return (
    (field.field_type === 'select' || field.field_type === 'dropdown') &&
    (field.allow_multiple === true || field.allow_multiple === 1 || field.allow_multiple === '1')
  );
};

const normalizeLoadedResponseValue = (field: any, value: string | null | undefined): FieldResponseValue => {
  if (value == null) return isMultiSelectField(field) ? [] : null;

  if (!isMultiSelectField(field)) return value;

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => String(entry ?? '').trim())
        .filter(Boolean);
    }
  } catch {
    // Fallbacks for legacy storage formats
  }

  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const serializeResponseValue = (value: FieldResponseValue): string | null => {
  if (Array.isArray(value)) {
    const cleaned = value.map((entry) => String(entry ?? '').trim()).filter(Boolean);
    return cleaned.length > 0 ? JSON.stringify(cleaned) : null;
  }

  if (value == null) return null;
  const normalized = String(value);
  return normalized.trim().length > 0 ? normalized : null;
};

const CompliancePageView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState<CompliancePage | null>(null);
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Structured form fields
  const [fields, setFields] = useState<any[]>([]);
  const [responses, setResponses] = useState<Record<number, FieldResponseValue>>({});
  const [savingFields, setSavingFields] = useState(false);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  // Calculated field live values keyed by field.id
  const [calculatedValues, setCalculatedValues] = useState<Record<number, {
    value: number | string | null;
    unit: string;
    period: string;
    source: string;
    computed_at: string;
    error?: string;
  }>>({});

  // Page locking
  const [lockStatus, setLockStatus] = useState<{ isLocked: boolean; lockedBy: number | null; lockedByName: string | null } | null>(null);

  // Optional expiry date captured at upload time so perishable evidence
  // (insurance, certifications, training) flows into the expiry dashboard
  // without a separate double-entry step.
  const [uploadExpiryDate, setUploadExpiryDate] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadPageData();
      loadFieldsAndResponses();
      checkLockStatus();
    }
    // Auto-lock when editing, unlock on leave
    return () => {
      if (id) {
        const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
        fetch(`/api/compliance/execution/pages/${id}/unlock`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-CSRF-Token': csrfToken,
          },
        }).catch(() => {});
      }
    };
  }, [id]);

  const checkLockStatus = async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/compliance/execution/pages/${id}/lock-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const json = await res.json();
        setLockStatus(json.data);
      }
    } catch {}
  };

  const acquireLock = async () => {
    if (!id) return;
    try {
      const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      await fetch(`/api/compliance/execution/pages/${id}/lock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-CSRF-Token': csrfToken,
        },
      });
      checkLockStatus();
    } catch {}
  };

  const loadFieldsAndResponses = async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/compliance/execution/pages/${id}/fields`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const json = await res.json();
        const loadedFields = json.data?.fields || [];
        const rawResponses = json.data?.responses || {};
        const calcValues = json.data?.calculatedValues || {};
        const normalizedResponses: Record<number, FieldResponseValue> = {};

        loadedFields.forEach((field: any) => {
          normalizedResponses[field.id] = normalizeLoadedResponseValue(field, rawResponses[field.id]);
        });

        setFields(loadedFields);
        setResponses(normalizedResponses);
        setCalculatedValues(calcValues);
      }
    } catch {
      // Non-critical — page still works without fields
    } finally {
      setFieldsLoaded(true);
    }
  };

  const handleSaveResponses = async () => {
    if (!id) return;
    setSavingFields(true);
    try {
      // Calculated fields: snapshot their live value into the response (audit trail),
      // but users don't edit them directly.
      const calculatedFieldIds = new Set(
        fields.filter((f: any) => f.field_type === 'calculated').map((f: any) => f.id)
      );
      const responseArray = Object.entries(responses)
        .filter(([fieldId]) => !calculatedFieldIds.has(parseInt(fieldId)))
        .map(([fieldId, value]) => ({
          fieldId: parseInt(fieldId),
          value: serializeResponseValue(value),
        }));
      // Snapshot calculated values as strings
      for (const fid of calculatedFieldIds) {
        const calc = calculatedValues[fid as number];
        if (calc && calc.value != null && !calc.error) {
          responseArray.push({
            fieldId: fid as number,
            value: `${calc.value} ${calc.unit} (${calc.period})`,
          });
        }
      }
      const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      const res = await fetch(`/api/compliance/execution/pages/${id}/fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ responses: responseArray }),
      });
      if (res.ok) {
        notify.success('Responses saved');
      } else {
        const errText = await res.text().catch(() => '');
        notify.error(`Failed to save responses${errText ? `: ${errText.slice(0, 120)}` : ''}`);
      }
    } catch {
      notify.error('Failed to save responses');
    } finally {
      setSavingFields(false);
    }
  };

  const loadPageData = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const [pageData, evidenceData] = await Promise.all([
        complianceExecutionApi.getCompliancePage(parseInt(id)),
        complianceExecutionApi.getEvidenceItems(parseInt(id))
      ]);
      setPage(pageData);
      setEvidence(evidenceData);
    } catch (error) {
      console.error('Error loading page data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    acquireLock();
    try {
      const uploadedEvidence = await complianceExecutionApi.uploadEvidence(parseInt(id), file, {
        expiresAt: uploadExpiryDate || null,
      });
      setEvidence(prev => [...prev, uploadedEvidence]);
      setUploadExpiryDate('');
      // reset the input so the same file can be re-selected later
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      notify.error('Failed to upload file');
    }
  };

  const handleUpdateEvidenceExpiry = async (evidenceId: number, expiresAt: string | null) => {
    try {
      const updated = await complianceExecutionApi.updateEvidenceExpiry(evidenceId, expiresAt);
      setEvidence(prev => prev.map(e => e.id === evidenceId ? updated : e));
    } catch (error) {
      console.error('Error updating expiry:', error);
      notify.error('Failed to update expiry date');
    }
  };

  const handleDeleteEvidence = async (evidenceId: number) => {
    try {
      await complianceExecutionApi.deleteEvidence(evidenceId);
      setEvidence(prev => prev.filter(e => e.id !== evidenceId));
    } catch (error) {
      console.error('Error deleting evidence:', error);
    }
  };

  const handleSubmitEvidence = async (evidenceId: number) => {
    try {
      const updatedEvidence = await complianceExecutionApi.submitEvidence(evidenceId);
      setEvidence(prev => prev.map(e => e.id === evidenceId ? updatedEvidence : e));
    } catch (error) {
      console.error('Error submitting evidence:', error);
    }
  };

  const handleSubmitPage = async () => {
    if (!id) return;

    setIsSubmitting(true);
    try {
      const updatedPage = await complianceExecutionApi.submitPage(parseInt(id));
      setPage(updatedPage);
    } catch (error) {
      console.error('Error submitting page:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadEvidence = async (evidenceId: number, fileName: string) => {
    try {
      const blob = await complianceExecutionApi.downloadEvidence(evidenceId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading evidence:', error);
    }
  };

  const handlePreviewEvidence = async (evidenceId: number) => {
    try {
      const blob = await complianceExecutionApi.downloadEvidence(evidenceId);

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing evidence:', error);
    }
  };

  const isPreviewable = (mimeType: string) => {
    const previewableTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'text/plain',
      'text/html'
    ];
    return previewableTypes.includes(mimeType);
  };

  const humanizeStatus = (status: string) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const getStatusBadge = (status: string) => {
    const pill = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-bold text-[10px] uppercase tracking-wide';
    switch (status) {
      case 'not_started':
        return <span className={`${pill} text-slate-600 bg-slate-100`}>
          <FileText size={12} />
          Not Started
        </span>;
      case 'pending':
      case 'in_progress':
        return <span className={`${pill} text-amber-700 bg-amber-100`}>
          <Clock size={12} />
          {humanizeStatus(status)}
        </span>;
      case 'submitted':
        return <span className={`${pill} text-blue-700 bg-blue-100`}>
          <CheckSquare size={12} />
          Submitted
        </span>;
      case 'approved_for_audit':
        return <span className={`${pill} text-indigo-700 bg-indigo-100`}>
          <CheckSquare size={12} />
          Approved for Audit
        </span>;
      case 'certified':
        return <span className={`${pill} text-emerald-700 bg-emerald-100`}>
          <CheckSquare size={12} />
          Certified
        </span>;
      case 'INTERNAL_VERIFIED':
        return <span className={`${pill} text-emerald-700 bg-emerald-100`}>
          <CheckSquare size={12} />
          Verified
        </span>;
      case 'REJECTED':
        return <span className={`${pill} text-red-700 bg-red-100`}>
          <AlertTriangle size={12} />
          Rejected
        </span>;
      case 'draft':
        return <span className={`${pill} text-slate-600 bg-slate-100`}>
          <FileText size={12} />
          Draft
        </span>;
      default:
        return <span className={`${pill} text-slate-600 bg-slate-100`}>
          <FileText size={12} />
          {humanizeStatus(status)}
        </span>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-red-700 bg-red-100">Critical</span>;
      case 'high':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-red-600 bg-red-50">High</span>;
      case 'medium':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-orange-700 bg-orange-100">Medium</span>;
      case 'low':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-slate-600 bg-slate-100">Low</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-slate-600 bg-slate-100">{priority}</span>;
    }
  };

  const isReadOnly = page?.status === 'certified' || page?.status === 'approved_for_audit' || page?.status === 'submitted';
  const canSubmitPage = !isReadOnly && evidence.length > 0 && evidence.some(e => e.status === 'submitted');
  const draftEvidence = evidence.filter(e => e.status === 'draft');
  const submittedEvidence = evidence.filter(e => e.status === 'submitted' || e.status === 'INTERNAL_VERIFIED');

  if (!hasPermission('compliance.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 text-sm">You don't have permission to view this compliance page.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading compliance page...</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8">
          <FileText className="text-slate-300 mb-3" size={40} />
          <h3 className="text-base font-semibold text-slate-900 mb-1">Page Not Found</h3>
          <p className="text-slate-500 text-sm">The compliance page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/compliance/tasks"
              className="p-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="text-slate-600" size={18} />
            </Link>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <h1 className="text-2xl font-bold text-slate-900 text-left truncate">{page.title}</h1>
                <PageInfoButton />
              </div>
              <p className="text-slate-500 text-sm text-left truncate">
                {page.framework_name ?? 'Compliance Page'} · {page.section}
              </p>
              {(page.clause_ref || page.source_ref) && (
                <p className="text-slate-500 text-xs text-left mt-1 font-mono">
                  {page.clause_ref ? `Clause ${page.clause_ref}` : null}
                  {page.clause_ref && page.source_ref && page.source_ref !== page.clause_ref ? ' · ' : null}
                  {page.source_ref && page.source_ref !== page.clause_ref ? `Source ${page.source_ref}` : null}
                  {!page.clause_ref && page.source_ref ? `Source ${page.source_ref}` : null}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getPriorityBadge(page.priority)}
            {getStatusBadge(page.status)}
            <button
              onClick={loadPageData}
              className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Lock Warning Banner */}
        {lockStatus?.isLocked && lockStatus.lockedByName && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
            <AlertTriangle className="text-amber-600 flex-shrink-0" size={18} />
            <p className="text-sm text-amber-800">
              This page is currently being edited by <strong>{lockStatus.lockedByName}</strong>. Your changes may be overwritten.
            </p>
          </div>
        )}

        {/* Page Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <div className="lg:col-span-2 space-y-4">
            {/* Page Information */}
            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h2 className="text-sm font-semibold text-slate-900 mb-4 text-left">Page Information</h2>
              <div className="space-y-4 text-left">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1 text-left">Description</label>
                  <p className="text-sm text-slate-700 text-left leading-relaxed">{page.description || 'No description provided'}</p>
                </div>

                {/* Guidance Section */}
                {page.guidance && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <FileText className="text-blue-600 mt-0.5 shrink-0" size={14} />
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-0.5">Guidance</label>
                        <p className="text-sm text-blue-800">{page.guidance}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Framework</label>
                    <p className="text-sm text-slate-700">{page.framework_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Section</label>
                    <p className="text-sm text-slate-700">{page.section}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Clause Reference</label>
                    <p className="text-sm text-slate-700 font-mono">{page.clause_ref || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Source Reference</label>
                    <p className="text-sm text-slate-700 font-mono">{page.source_ref || page.clause_ref || 'N/A'}</p>
                  </div>
                </div>

                {/* Evidence Requirements */}
                {page.evidence_required && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={14} />
                      <div>
                        <label className="block text-xs font-medium text-amber-900 mb-0.5">Evidence Required</label>
                        <p className="text-sm text-amber-800">
                          {page.evidence_type === 'document' && 'Document evidence is required for this compliance page.'}
                          {page.evidence_type === 'number' && 'Numeric evidence is required for this compliance page.'}
                          {page.evidence_type === 'text' && 'Text-based evidence is required for this compliance page.'}
                          {page.evidence_type === 'mixed' && 'Multiple types of evidence are required for this compliance page.'}
                          {!page.evidence_type && 'Evidence is required for this compliance page.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Due Date</label>
                    <div className="flex items-center gap-1.5">
                      <Calendar size={14} className="text-slate-400" />
                      <span className="text-sm text-slate-700">
                        {page.due_date ? new Date(page.due_date).toLocaleDateString() : 'No due date'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Last Updated</label>
                    <span className="text-sm text-slate-700">{new Date(page.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Structured Form Fields.
                A field is "structured" unless its field_type is a file-upload
                type (document/file/attachment). The legacy filter also
                dropped every is_evidence=true field, but dropdowns and
                yes/no answers marked as evidence are still structured
                questions the user has to answer — hiding them broke the
                compliance page renderer for every framework that used
                the "Evidence Field" toggle in the builder on a non-file
                question. Only true document uploaders belong in the
                Supporting Evidence file-upload section below. */}
            {(() => {
              const structuredFields = (fields as any[]).filter(
                (f) => !['document', 'file', 'attachment'].includes(String(f.field_type).toLowerCase()),
              );
              if (!fieldsLoaded || structuredFields.length === 0) return null;
              return (
              <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-slate-900">Compliance Questions</h2>
                  <button
                    onClick={handleSaveResponses}
                    disabled={savingFields}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {savingFields ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save Responses</>}
                  </button>
                </div>
                <div className="space-y-4">
                  {structuredFields.map((field: any) => {
                    const responseValue = responses[field.id];
                    const stringValue = typeof responseValue === 'string' ? responseValue : '';
                    const selectOptions = parseFieldOptions(field.field_options);
                    const isMultiSelect = isMultiSelectField(field);
                    const multiValue = Array.isArray(responseValue) ? responseValue : [];

                    return (
                      <div key={field.id} className="space-y-1.5">
                        <label className="block text-sm font-medium text-slate-700">
                          {(() => {
                            // Guard against legacy fields where `label` was
                            // left as the bare field type ("Boolean", "Document",
                            // etc.) — those aren't real questions. Fall back
                            // to field_name (the user's typed question).
                            const typeWords = new Set(['boolean', 'document', 'text', 'number', 'date', 'select', 'multiselect', 'textarea', 'file', 'calculated']);
                            const raw = field.label || field.field_name || '';
                            if (raw && typeWords.has(String(raw).toLowerCase())) {
                              return field.field_name || raw;
                            }
                            return raw;
                          })()}
                          {field.is_required ? <span className="text-red-500 ml-1">*</span> : ''}
                        </label>
                        {field.description && (
                          <p className="text-xs text-slate-500">{field.description}</p>
                        )}

                        {/* Text input */}
                        {(field.field_type === 'text' || field.field_type === 'short_text') && (
                          <input
                            type="text"
                            value={stringValue}
                            onChange={(e) => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                            placeholder={field.placeholder || ''}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                          />
                        )}

                        {/* Long text / textarea */}
                        {(field.field_type === 'long_text' || field.field_type === 'textarea') && (
                          <textarea
                            value={stringValue}
                            onChange={(e) => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                            placeholder={field.placeholder || ''}
                            rows={3}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                          />
                        )}

                        {/* Number */}
                        {field.field_type === 'number' && (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={stringValue}
                              onChange={(e) => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                              placeholder={field.placeholder || '0'}
                              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                            />
                            {field.unit && <span className="text-sm text-slate-500 flex-shrink-0">{field.unit}</span>}
                          </div>
                        )}

                        {/* Boolean / Yes-No */}
                        {(field.field_type === 'boolean' || field.field_type === 'yes_no') && (
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`field-${field.id}`}
                                checked={stringValue === 'true' || stringValue === 'yes'}
                                onChange={() => setResponses(prev => ({ ...prev, [field.id]: 'true' }))}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm text-slate-700">Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`field-${field.id}`}
                                checked={stringValue === 'false' || stringValue === 'no'}
                                onChange={() => setResponses(prev => ({ ...prev, [field.id]: 'false' }))}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm text-slate-700">No</span>
                            </label>
                          </div>
                        )}

                        {/* Select / Dropdown - Single Select */}
                        {(field.field_type === 'select' || field.field_type === 'dropdown') && !isMultiSelect && (
                          <select
                            value={stringValue}
                            onChange={(e) => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                          >
                            <option value="">Select...</option>
                            {selectOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}

                        {/* Select / Dropdown - Multi Select */}
                        {isMultiSelect && (
                          <div className="space-y-1.5">
                            <select
                              multiple
                              value={multiValue}
                              onChange={(e) => {
                                const selectedValues = Array.from(e.target.selectedOptions).map((option) => option.value);
                                setResponses(prev => ({ ...prev, [field.id]: selectedValues }));
                              }}
                              size={Math.min(Math.max(selectOptions.length, 3), 6)}
                              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                            >
                              {selectOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <p className="text-[11px] text-slate-500">
                              Hold Ctrl (Windows) or Cmd (Mac) to choose multiple options.
                            </p>
                          </div>
                        )}

                        {/* Date */}
                        {field.field_type === 'date' && (
                          <input
                            type="date"
                            value={stringValue}
                            onChange={(e) => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                          />
                        )}

                        {/* Calculated - live-resolved from emissions data */}
                        {field.field_type === 'calculated' && (() => {
                          const calc = calculatedValues[field.id];
                          const displayValue = calc?.value ?? '—';
                          const displayUnit = calc?.unit ?? '';
                          const isError = !!calc?.error;
                          return (
                            <div className={`rounded-xl border p-4 ${isError ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                              <div className="flex items-baseline justify-between gap-3">
                                <div>
                                  <div className={`text-2xl font-semibold ${isError ? 'text-amber-700' : 'text-emerald-700'}`}>
                                    {typeof displayValue === 'number' ? displayValue.toLocaleString(undefined, { maximumFractionDigits: 3 }) : displayValue}
                                    {displayUnit && <span className="text-sm font-medium ml-1.5 text-slate-600">{displayUnit}</span>}
                                  </div>
                                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">
                                    {isError ? (calc?.error || 'Unavailable') : `Live from Emissions · ${calc?.period || ''}`}
                                  </div>
                                </div>
                                <div className="text-[11px] text-slate-400">
                                  {calc?.source}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Fallback for unknown types */}
                        {!['text', 'short_text', 'long_text', 'textarea', 'number', 'boolean', 'yes_no', 'select', 'dropdown', 'multiselect', 'date', 'calculated'].includes(field.field_type) && (
                          <input
                            type="text"
                            value={stringValue}
                            onChange={(e) => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                            placeholder={field.placeholder || `Enter ${field.field_type || 'value'}...`}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}

            {/* Evidence Upload */}
            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-slate-900">Supporting Evidence</h2>
                {!isReadOnly && (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <label htmlFor="evidence-expiry" className="text-[10px] text-slate-500 leading-none mb-0.5">Expires on (optional)</label>
                      <input
                        id="evidence-expiry"
                        type="date"
                        value={uploadExpiryDate}
                        onChange={(e) => setUploadExpiryDate(e.target.value)}
                        className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                        title="Optional — for evidence that expires (insurance, certifications, training records). Auto-creates expiry warnings."
                      />
                    </div>
                    <label className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 cursor-pointer transition-colors flex items-center gap-2">
                      <Upload size={16} />
                      Upload File
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      />
                    </label>
                  </div>
                )}
              </div>

              {evidence.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 bg-slate-50/40">
                  <div className="flex items-start gap-4 text-left">
                    <div className="p-2.5 bg-white border border-slate-200 rounded-lg flex-shrink-0">
                      <Paperclip className="text-slate-400" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-slate-800">No evidence uploaded yet</h3>
                      <p className="text-xs text-slate-500 mt-1">
                        Use the <strong>Upload File</strong> button above to attach supporting documents (PDF, Word, Excel, image). You can upload multiple files — each one will appear here.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {evidence.map(item => {
                    const expiry = item.expires_at ? new Date(item.expires_at) : null;
                    const daysToExpiry = expiry ? Math.floor((expiry.getTime() - Date.now()) / 86400000) : null;
                    const expiryTone =
                      daysToExpiry == null ? '' :
                      daysToExpiry < 0 ? 'bg-red-50 text-red-700 border-red-200' :
                      daysToExpiry <= 30 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-emerald-50 text-emerald-700 border-emerald-200';
                    const expiryLabel =
                      daysToExpiry == null ? null :
                      daysToExpiry < 0 ? `Expired ${Math.abs(daysToExpiry)}d ago` :
                      daysToExpiry === 0 ? 'Expires today' :
                      `Expires in ${daysToExpiry}d`;
                    return (
                    <div key={item.id} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-slate-50 rounded-lg">
                            <FileText className="text-slate-500" size={16} />
                          </div>
                          <div>
                            <h4 className="font-medium text-sm text-slate-900">{item.file_name}</h4>
                            <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                              <span>{(item.file_size / 1024).toFixed(1)} KB</span>
                              <span>{item.mime_type}</span>
                              <span>Uploaded: {new Date(item.created_at).toLocaleDateString()}</span>
                              {item.first_name && item.last_name && (
                                <span>By: {item.first_name} {item.last_name}</span>
                              )}
                              {expiryLabel && (
                                <span className={`px-1.5 py-0.5 rounded border text-[11px] font-medium ${expiryTone}`}>
                                  {expiryLabel} ({expiry!.toLocaleDateString()})
                                </span>
                              )}
                            </div>
                            {!isReadOnly && (
                              <div className="mt-1.5 flex items-center gap-2 text-xs">
                                <label className="text-[10px] text-slate-400">Expiry:</label>
                                <input
                                  type="date"
                                  defaultValue={item.expires_at ? item.expires_at.slice(0, 10) : ''}
                                  onChange={(e) => handleUpdateEvidenceExpiry(item.id, e.target.value || null)}
                                  className="px-1.5 py-0.5 border border-slate-200 rounded text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-200"
                                  title="Set or change the expiry date for this evidence"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {getStatusBadge(item.status)}
                          {isPreviewable(item.mime_type) && (
                            <button
                              onClick={() => handlePreviewEvidence(item.id)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Preview"
                            >
                              <Eye size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDownloadEvidence(item.id, item.file_name)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                          {item.status === 'draft' && (
                            <>
                              <button
                                onClick={() => handleSubmitEvidence(item.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Submit Evidence"
                              >
                                <CheckSquare size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteEvidence(item.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {item.notes && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <p className="text-xs text-slate-500">{item.notes}</p>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Findings Journal — temporarily disabled.
              The component is wired but the backend `audit_findings` table schema
              doesn't match what the service tries to INSERT (missing columns:
              compliance_page_id, auditor_id, severity, recommendation; enum mismatch
              on status; missing updated_at). Posting a finding would throw a SQL error.
              Re-enable in the dedicated follow-up session — see
              docs/COMPLIANCE_LIFECYCLE_PLAN.md §13 "Admin Escape-Hatch Console" notes. */}
          {/* {page && <FindingsJournal compliancePageId={page.id} />} */}

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Status Summary */}
            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h3 className="text-sm font-semibold text-slate-900 mb-3 text-left">Status Summary</h3>
              <div className="space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500 text-left">Total Evidence</span>
                  <span className="text-sm font-medium text-slate-900">{evidence.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500 text-left">Draft</span>
                  <span className="text-sm font-medium text-blue-600">{draftEvidence.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500 text-left">Submitted</span>
                  <span className="text-sm font-medium text-green-600">{submittedEvidence.length}</span>
                </div>
              </div>
            </div>

            {/* Actions — always visible so users know where the Submit button lives,
                even before they've uploaded anything. Disabled state with a clear reason. */}
            {!isReadOnly && (() => {
              const submitBlocker =
                evidence.length === 0
                  ? 'Upload at least one evidence file first.'
                  : !evidence.some((e) => e.status === 'submitted')
                  ? 'Click the ✓ button next to each uploaded file to mark it as ready, then submit.'
                  : null;
              const enabled = submitBlocker === null;
              return (
                <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 text-left">Submit for Review</h3>
                  <button
                    onClick={handleSubmitPage}
                    disabled={!enabled || isSubmitting}
                    className={`w-full px-4 py-2.5 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      enabled
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-slate-300 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <><RefreshCw size={16} className="animate-spin" /> Submitting…</>
                    ) : (
                      <><CheckSquare size={16} /> Submit Page for Internal Review</>
                    )}
                  </button>
                  <p className="text-xs text-slate-500 mt-2 text-left leading-relaxed">
                    {submitBlocker ?? 'Internal auditor will review your evidence and approve or request changes.'}
                  </p>
                </div>
              );
            })()}

            {/* Read-only banner when page is already submitted/certified */}
            {isReadOnly && (
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 text-left">
                <h3 className="text-sm font-semibold text-slate-800 mb-1">Page is read-only</h3>
                <p className="text-xs text-slate-500">
                  This page is currently <strong>{humanizeStatus(page.status)}</strong> and cannot be edited. Wait for the auditor review or request changes from your company admin.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompliancePageView;
